import React, { useState } from 'react';
import { Card, Row, Col, Statistic, Table, Button, Space, Typography, Tooltip, theme } from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  CalendarOutlined,
  AimOutlined,
  MinusCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { DualAxes } from '@ant-design/charts';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../utils/formatters';
import type { ForecastSummaryData } from './ForecastAmazonUS';

interface SummaryProps {
  forecastSummary?: ForecastSummaryData | null;
}

const Summary: React.FC<SummaryProps> = ({ forecastSummary }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [showChart, setShowChart] = useState(true);

  if (!forecastSummary) {
    return (
      <Card title={t('idea_calc.summary.title')} bordered loading />
    );
  }

  // forecastSummary is guaranteed non-null after the guard above
  const summary = forecastSummary;

  const columns = [
    {
      title: t('idea_calc.summary.col_metric'),
      dataIndex: 'metric',
      key: 'metric',
      fixed: 'left' as const,
      width: 180,
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
    },
    ...summary.yearlyData.map((year) => ({
      title: year.year,
      dataIndex: year.year,
      key: year.year,
      width: 120,
      align: 'center' as const,
    })),
    {
      title: t('idea_calc.summary.col_total'),
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'center' as const,
      className: 'ant-table-cell-total',
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
    },
  ];

  // Standard value coloring: green positive, red negative
  const colorVal = (v: number, formatted: string) => (
    <span style={{ color: v >= 0 ? token.colorSuccess : token.colorError }}>{formatted}</span>
  );

  // Expense coloring: orange for costs (positive cost = orange, negative = green)
  const colorExpense = (v: number, formatted: string) => (
    <span style={{ color: v > 0 ? token.colorWarning : token.colorSuccess }}>{formatted}</span>
  );

  const fmtM = (v: number) => formatMoney(v);

  // Row background colors for visual grouping
  const ROW_COLORS = {
    revenue: '#f6ffed',      // light green
    plPositive: '#f6ffed',   // light green
    plNegative: '#fff2f0',   // light red
    expense: '#fffbe6',      // light yellow/orange
    profit: '#e6f7ff',       // light blue
    percent: '#f9f0ff',      // light purple
  };

  const makeRow = (metric: React.ReactNode, key: string, getter: (y: typeof summary.yearlyData[0]) => number, totalVal: number, isMoney = true, rowColor?: string) => {
    const row: Record<string, React.ReactNode> = { key, metric, _rowColor: rowColor };
    summary.yearlyData.forEach((y) => {
      const v = getter(y);
      row[y.year] = isMoney ? colorVal(v, fmtM(v)) : colorVal(v, `${v}%`);
    });
    row.total = isMoney ? colorVal(totalVal, fmtM(totalVal)) : colorVal(totalVal, `${totalVal}%`);
    return row;
  };

  const makeExpenseRow = (metric: React.ReactNode, key: string, getter: (y: typeof summary.yearlyData[0]) => number, totalVal: number, rowColor?: string) => {
    const row: Record<string, React.ReactNode> = { key, metric, _rowColor: rowColor };
    summary.yearlyData.forEach((y) => {
      const v = getter(y);
      row[y.year] = colorExpense(v, fmtM(v));
    });
    row.total = colorExpense(totalVal, fmtM(totalVal));
    return row;
  };

  // Table row labels with tooltips
  const metricWithTooltip = (label: string, tip: string) => (
    <Tooltip title={tip}><span style={{ cursor: 'help' }}>{label}</span></Tooltip>
  );

  const tableData = [
    makeRow(metricWithTooltip(t('idea_calc.summary.row_revenue'), t('idea_calc.summary.row_revenue_tip')), 'revenue', (y) => y.revenue, summary.totalData.revenue, true, ROW_COLORS.revenue),
    makeRow(metricWithTooltip(t('idea_calc.summary.row_pl'), t('idea_calc.summary.row_pl_tip')), 'pl', (y) => y.pl, summary.totalData.pl, true, summary.totalData.pl >= 0 ? ROW_COLORS.plPositive : ROW_COLORS.plNegative),
    makeRow(metricWithTooltip(t('idea_calc.summary.row_cf'), t('idea_calc.summary.row_cf_tip')), 'cf', (y) => y.cashFlow, summary.totalData.cashFlow, true, summary.totalData.cashFlow >= 0 ? ROW_COLORS.plPositive : ROW_COLORS.plNegative),
    makeExpenseRow(metricWithTooltip(t('idea_calc.summary.row_ads'), t('idea_calc.summary.row_ads_tip')), 'ads', (y) => y.adSpend, summary.totalData.adSpend, ROW_COLORS.expense),
    makeExpenseRow(metricWithTooltip(t('idea_calc.summary.row_fixed'), t('idea_calc.summary.row_fixed_tip')), 'fixed', (y) => y.fixedCosts, summary.totalData.fixedCosts, ROW_COLORS.expense),
    makeRow(metricWithTooltip(t('idea_calc.summary.row_profit'), t('idea_calc.summary.row_profit_tip')), 'profit', (y) => y.profit, summary.totalData.profit, true, ROW_COLORS.profit),
    makeRow(metricWithTooltip(t('idea_calc.summary.row_profit_no_ads'), t('idea_calc.summary.row_profit_no_ads_tip')), 'profitNoAds', (y) => y.profitWithoutAds, summary.totalData.profitWithoutAds, true, ROW_COLORS.profit),
    makeRow(metricWithTooltip(t('idea_calc.summary.row_margin'), t('idea_calc.summary.row_margin_tip')), 'margin', (y) => y.avgMargin, summary.totalData.avgMargin, false, ROW_COLORS.percent),
    makeRow(metricWithTooltip(t('idea_calc.summary.row_roi'), t('idea_calc.summary.row_roi_tip')), 'roi', (y) => y.avgROI, summary.totalData.avgROI, false, ROW_COLORS.percent),
  ];

  // Use monthly data for chart (first 12 months only - 1 year forecast)
  const monthlyChartData = (summary.monthlyData || []).slice(0, 12).map((m) => ({
    month: m.month,
    profit: m.profit,
    cumPL: m.cumPL,
    cumCF: m.cumCF,
  }));

  // Field names for chart (translated)
  const profitField = t('idea_calc.summary.chart_series_profit');
  const plCumField = t('idea_calc.summary.chart_series_pl_cum');
  const cfCumField = t('idea_calc.summary.chart_series_cf_cum');

  const chartData = monthlyChartData.map((m) => ({
    month: m.month,
    [profitField]: m.profit,
    [plCumField]: m.cumPL,
    [cfCumField]: m.cumCF,
  }));

  const dualAxesConfig = {
    xField: 'month',
    data: chartData,
    height: 340,
    legend: false, // Use custom legend below
    children: [
      {
        type: 'interval' as const,
        yField: profitField,
        style: {
          fill: (datum: Record<string, number>) => datum[profitField] >= 0 ? '#52c41a' : '#ff4d4f',
          fillOpacity: 0.7,
        },
        axis: {
          y: { title: t('idea_calc.summary.chart_y_profit'), titleFill: '#595959' },
        },
      },
      {
        type: 'line' as const,
        yField: plCumField,
        shapeField: 'smooth',
        scale: { y: { independent: true } },
        axis: {
          y: { position: 'right' as const, title: t('idea_calc.summary.chart_y_cumulative'), titleFill: '#1890ff', grid: null },
        },
        style: { stroke: '#1890ff', lineWidth: 2 },
      },
      {
        type: 'line' as const,
        yField: cfCumField,
        shapeField: 'smooth',
        scale: { y: { key: plCumField } }, // Share axis with P&L
        style: { stroke: '#722ed1', lineWidth: 2, lineDash: [4, 4] },
      },
    ],
    axis: {
      x: { label: { autoRotate: true, autoHide: true } },
    },
    tooltip: {
      title: 'month',
      items: [
        { field: profitField, name: t('idea_calc.summary.chart_tooltip_profit'), valueFormatter: (v: number) => `$${v.toLocaleString()}` },
        { field: plCumField, name: t('idea_calc.summary.chart_tooltip_pl_cum'), valueFormatter: (v: number) => `$${v.toLocaleString()}` },
        { field: cfCumField, name: t('idea_calc.summary.chart_tooltip_cf_cum'), valueFormatter: (v: number) => `$${v.toLocaleString()}` },
      ],
    },
  };

  return (
    <Card title={t('idea_calc.summary.title')} bordered>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={5}>
          <Tooltip title={t('idea_calc.summary.kpi_cogs_tip')}>
            <Card size="small" style={{ background: '#e6f4ff', borderColor: '#91caff' }}>
              <Statistic
                title={<><DollarOutlined /> {t('idea_calc.summary.kpi_cogs')}</>}
                value={summary.cogsPerUnit}
                precision={2}
                prefix="$"
                valueStyle={{ color: token.colorPrimary }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Tooltip title={t('idea_calc.summary.kpi_price_tip')}>
            <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
              <Statistic
                title={<><RiseOutlined /> {t('idea_calc.summary.kpi_price')}</>}
                value={summary.sellingPrice}
                precision={2}
                prefix="$"
                valueStyle={{ color: token.colorSuccess }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Tooltip title={t('idea_calc.summary.kpi_break_even_pl_tip')}>
            <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
              <Statistic
                title={<><CalendarOutlined /> {t('idea_calc.summary.kpi_break_even_pl')}</>}
                value={summary.positiveMonthPL > 0 ? summary.positiveMonthPL : 0}
                suffix={summary.positiveMonthPL > 0 ? t('idea_calc.summary.kpi_months_suffix') : undefined}
                formatter={(v) => (Number(v) > 0 ? `${v}` : t('idea_calc.summary.kpi_not_reached'))}
                valueStyle={{ color: token.colorWarning }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Tooltip title={t('idea_calc.summary.kpi_break_even_cf_tip')}>
            <Card size="small" style={{ background: '#f9f0ff', borderColor: '#d3adf7' }}>
              <Statistic
                title={<><AimOutlined /> {t('idea_calc.summary.kpi_break_even_cf')}</>}
                value={summary.positiveMonthCF > 0 ? summary.positiveMonthCF : 0}
                suffix={summary.positiveMonthCF > 0 ? t('idea_calc.summary.kpi_months_suffix') : undefined}
                formatter={(v) => (Number(v) > 0 ? `${v}` : t('idea_calc.summary.kpi_not_reached'))}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Tooltip title={t('idea_calc.summary.kpi_min_cf_tip')}>
            <Card size="small" style={{ background: '#fff2f0', borderColor: '#ffccc7' }}>
              <Statistic
                title={<><MinusCircleOutlined /> {t('idea_calc.summary.kpi_min_cf')}</>}
                value={summary.minCashFlow}
                prefix="$"
                valueStyle={{ color: token.colorError }}
              />
            </Card>
          </Tooltip>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 900 }}
        onRow={(record) => ({
          style: { background: (record as Record<string, unknown>)._rowColor as string || undefined },
        })}
      />

      <div style={{ marginTop: 16 }}>
        <Button
          type="default"
          icon={showChart ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={() => setShowChart(!showChart)}
        >
          {showChart ? t('idea_calc.summary.hide_chart') : t('idea_calc.summary.show_chart')}
        </Button>
      </div>

      {showChart && (
        <div style={{ marginTop: 16, position: 'relative', zIndex: 10 }}>
          <div style={{ marginBottom: 8 }}>
            <Space wrap>
              <Tooltip title={t('idea_calc.summary.chart_profit_pos_tip')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
                  <div style={{ width: 12, height: 12, background: '#52c41a', borderRadius: 2 }} />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.summary.chart_profit_pos')}</Typography.Text>
                </div>
              </Tooltip>
              <Tooltip title={t('idea_calc.summary.chart_profit_neg_tip')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
                  <div style={{ width: 12, height: 12, background: '#ff4d4f', borderRadius: 2 }} />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.summary.chart_profit_neg')}</Typography.Text>
                </div>
              </Tooltip>
              <Tooltip title={t('idea_calc.summary.chart_pl_cum_tip')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
                  <div style={{ width: 16, height: 2, background: '#1890ff' }} />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.summary.chart_pl_cum')}</Typography.Text>
                </div>
              </Tooltip>
              <Tooltip title={t('idea_calc.summary.chart_cf_cum_tip')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
                  <div style={{ width: 16, height: 2, background: '#722ed1', borderStyle: 'dashed', borderWidth: '1px 0 0 0', borderColor: '#722ed1' }} />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.summary.chart_cf_cum')}</Typography.Text>
                </div>
              </Tooltip>
            </Space>
          </div>
          <DualAxes {...dualAxesConfig} />
        </div>
      )}
    </Card>
  );
};

export default React.memo(Summary);
